import { MASTER_DATA_CONFIG, buildEndpointUrl, buildSearchEndpointUrl, getEndpointConfig } from '../constants/masterDataEndpoints';
import axios from 'axios';

/**
 * Make axios request with proper error handling
 * @param {string} url - The URL to fetch
 * @param {Object} options - Request options
 * @returns {Promise<Object>} - Axios response data
 */
const makeAxiosRequest = async (url, options = {}) => {
  try {
    // Use axios which will automatically include authorization headers via interceptors
    const response = await axios.get(url, {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers
      },
      ...options
    });
    
    // Handle different response structures
    if (response.data && typeof response.data === 'object') {
      // If response.data has a success field, it's likely our API response
      if (response.data.success !== undefined) {
        return response.data;
      }
      // If response.data is directly an array, wrap it in expected structure
      else if (Array.isArray(response.data)) {
        return {
          success: true,
          data: response.data,
          total: response.count,
          page: 1,
          limit: response.data.length
        };
      }
      
      // If response.data has data field, return as is
      else if (response.data.data !== undefined) {
        return response.data;
      }
      // Otherwise, wrap the entire response in expected structure
      else {
        return {
          success: true,
          data: response.data,
          total: 1,
          page: 1,
          limit: 1
        };
      }
    }
    
    return response.data;
  } catch (error) {
    // Provide specific error messages for common issues
    if (error.response) {
      // Server responded with error status
      throw new Error(`HTTP error! status: ${error.response.status} - ${error.response.data?.message || error.response.statusText}`);
    } else if (error.request) {
      // Request was made but no response received
      throw new Error('Network error: No response from server. Check if the API server is running and accessible.');
    } else {
      // Something else happened
      throw error;
    }
  }
};

/**
 * Generic function to fetch paginated master data
 * @param {string} endpointKey - The API endpoint key
 * @param {Object} options - Additional options
 * @param {number} options.page - Page number
 * @param {number} options.limit - Items per page
 * @param {Object} options.params - Additional query parameters
 * @param {string} options.search - Search term
 * @param {boolean} options.useSearchEndpoint - Whether to use search endpoint
 * @returns {Promise<Object>} - Response data with pagination info
 */
export const fetchMasterData = async (endpointKey, options = {}) => {
  try {
    const config = getEndpointConfig(endpointKey);
    if (!config) {
      throw new Error(`Unknown endpoint: ${endpointKey}`);
    }

    const { 
      page = MASTER_DATA_CONFIG.PAGINATION.DEFAULT_PAGE, 
      limit = config.defaultLimit, 
      params = {}, 
      search = '',
      useSearchEndpoint = false 
    } = options;

    // Build the full URL - use search endpoint if search term is provided or explicitly requested
    const url = (search && search.trim()) || useSearchEndpoint 
      ? buildSearchEndpointUrl(endpointKey, { page, limit, params, search })
      : buildEndpointUrl(endpointKey, { page, limit, params, search });

    // Use axios request which includes authorization headers
    const data = await makeAxiosRequest(url, {
      // Additional axios options can be passed here if needed
    });

    return data;
  } catch (error) {
    throw error;
  }
};

/**
 * Fetch all pages of master data
 * @param {string} endpointKey - The API endpoint key
 * @param {Object} options - Additional options
 * @param {number} options.maxPages - Maximum pages to fetch
 * @param {Object} options.params - Additional parameters for API calls
 * @returns {Promise<Array>} - Array of all data items
 */
export const fetchAllMasterData = async (endpointKey, options = {}) => {
  try {
    const config = getEndpointConfig(endpointKey);
    if (!config) {
      throw new Error(`Unknown endpoint: ${endpointKey}`);
    }

    const { maxPages = MASTER_DATA_CONFIG.PAGINATION.MAX_PAGES, params = {} } = options;
    let allData = [];
    let currentPage = 1;
    let hasMoreData = true;

    while (hasMoreData && currentPage <= maxPages) {
      try {
        const response = await fetchMasterData(endpointKey, {
          page: currentPage,
          limit: config.defaultLimit,
          params
        });

        if (response && response[MASTER_DATA_CONFIG.RESPONSE_STRUCTURE.DATA_FIELD]) {
          const newData = response[MASTER_DATA_CONFIG.RESPONSE_STRUCTURE.DATA_FIELD];
          // Get total count from response (use count field if available, otherwise total)
          const totalCount = response[MASTER_DATA_CONFIG.RESPONSE_STRUCTURE.COUNT_FIELD] || 
                            response[MASTER_DATA_CONFIG.RESPONSE_STRUCTURE.TOTAL_FIELD];

          if (Array.isArray(newData)) {
            allData = [...allData, ...newData];

            // Check if we have more data using count field
            if (totalCount !== null && totalCount !== undefined) {
              hasMoreData = allData.length < totalCount;
            } else {
              // Fallback: If we got less than the requested limit, there are no more pages
              hasMoreData = newData.length === config.defaultLimit;
            }
          } else {
            hasMoreData = false;
          }
        } else {
          hasMoreData = false;
        }

        currentPage++;
      } catch (error) {
        hasMoreData = false;
      }
    }

    return allData;
  } catch (error) {
    return [];
  }
};

// Specific functions for each master data type
export const MasterDataService = {
  // Test endpoint to verify API connectivity
  testConnection: () =>
    fetchMasterData('test', { page: 1, limit: 1 }),

  // Languages
  getLanguages: (page = 1, limit = 10) =>
    fetchMasterData('languages', { page, limit }),

  // Marital Statuses
  getMaritalStatuses: (page = 1, limit = 10) =>
    fetchMasterData('maritalStatuses', { page, limit }),

  // Religions
  getReligions: (page = 1, limit = 10) =>
    fetchMasterData('religions', { page, limit }),

  // Castes (with division code support)
  getCastes: (page = 1, limit = 10, divisionCode = null) => {
    const params = divisionCode ? { division_code: divisionCode } : {};
    return fetchMasterData('castes', { page, limit, params });
  },

  // Heights
  getHeights: (page = 1, limit = 10) =>
    fetchMasterData('heights', { page, limit }),

  // Birth Stars
  getBirthStars: (page = 1, limit = 10) =>
    fetchMasterData('birthStars', { page, limit }),

  // Jobs
  getJobs: (page = 1, limit = 10) =>
    fetchMasterData('jobs', { page, limit }),

  // Educations
  getEducations: (page = 1, limit = 10) =>
    fetchMasterData('educations', { page, limit }),

  // Interests
  getInterests: (page = 1, limit = 10) =>
    fetchMasterData('interests', { page, limit }),

  // Weights
  getWeights: (page = 1, limit = 10) =>
    fetchMasterData('weights', { page, limit }),

  // Manglik Lists
  getManglikLists: (page = 1, limit = 10) =>
    fetchMasterData('manglikLists', { page, limit }),

  // Food
  getFood: (page = 1, limit = 10) =>
    fetchMasterData('food', { page, limit }),

  // Drinking
  getDrinking: (page = 1, limit = 10) =>
    fetchMasterData('drinking', { page, limit }),

  // Smoking
  getSmoking: (page = 1, limit = 10) =>
    fetchMasterData('smoking', { page, limit }),

  // Ideologies
  getIdeologies: (page = 1, limit = 10) =>
    fetchMasterData('ideologies', { page, limit }),

  // Family Types
  getFamilyTypes: (page = 1, limit = 10) =>
    fetchMasterData('familyTypes', { page, limit }),

  // Get all data functions (for initial load)
  getAllLanguages: () =>
    fetchAllMasterData('languages'),

  getAllMaritalStatuses: () =>
    fetchAllMasterData('maritalStatuses'),

  getAllReligions: () =>
    fetchAllMasterData('religions'),

  getAllCastes: (divisionCode = null) =>
    fetchAllMasterData('castes', { params: divisionCode ? { division_code: divisionCode } : {} }),

  getAllHeights: () =>
    fetchAllMasterData('heights'),

  getAllBirthStars: () =>
    fetchAllMasterData('birthStars'),

  getAllJobs: () =>
    fetchAllMasterData('jobs'),

  getAllEducations: () =>
    fetchAllMasterData('educations'),

  getAllInterests: () =>
    fetchAllMasterData('interests'),

  getAllWeights: () =>
    fetchAllMasterData('weights'),

  getAllManglikLists: () =>
    fetchAllMasterData('manglikLists'),

  getAllFood: () =>
    fetchAllMasterData('food'),

  getAllDrinking: () =>
    fetchAllMasterData('drinking'),

  getAllSmoking: () =>
    fetchAllMasterData('smoking'),

  getAllIdeologies: () =>
    fetchAllMasterData('ideologies'),

  getAllFamilyTypes: () =>
    fetchAllMasterData('familyTypes'),

  // Search functions for each master data type
  searchLanguages: (searchTerm, page = 1, limit = 10) =>
    fetchMasterData('languages', { page, limit, search: searchTerm, useSearchEndpoint: true }),

  searchMaritalStatuses: (searchTerm, page = 1, limit = 10) =>
    fetchMasterData('maritalStatuses', { page, limit, search: searchTerm, useSearchEndpoint: true }),

  searchReligions: (searchTerm, page = 1, limit = 10) =>
    fetchMasterData('religions', { page, limit, search: searchTerm, useSearchEndpoint: true }),

  searchCastes: (searchTerm, page = 1, limit = 10, divisionCode = null) => {
    const params = divisionCode ? { division_code: divisionCode } : {};
    return fetchMasterData('castes', { page, limit, search: searchTerm, params, useSearchEndpoint: true });
  },

  searchHeights: (searchTerm, page = 1, limit = 10) =>
    fetchMasterData('heights', { page, limit, search: searchTerm, useSearchEndpoint: true }),

  searchBirthStars: (searchTerm, page = 1, limit = 10) =>
    fetchMasterData('birthStars', { page, limit, search: searchTerm, useSearchEndpoint: true }),

  searchJobs: (searchTerm, page = 1, limit = 10) =>
    fetchMasterData('jobs', { page, limit, search: searchTerm, useSearchEndpoint: true }),

  searchEducations: (searchTerm, page = 1, limit = 10) =>
    fetchMasterData('educations', { page, limit, search: searchTerm, useSearchEndpoint: true }),

  searchInterests: (searchTerm, page = 1, limit = 10) =>
    fetchMasterData('interests', { page, limit, search: searchTerm, useSearchEndpoint: true }),

  searchWeights: (searchTerm, page = 1, limit = 10) =>
    fetchMasterData('weights', { page, limit, search: searchTerm, useSearchEndpoint: true }),

  searchManglikLists: (searchTerm, page = 1, limit = 10) =>
    fetchMasterData('manglikLists', { page, limit, search: searchTerm, useSearchEndpoint: true }),

  searchFood: (searchTerm, page = 1, limit = 10) =>
    fetchMasterData('food', { page, limit, search: searchTerm, useSearchEndpoint: true }),

  searchDrinking: (searchTerm, page = 1, limit = 10) =>
    fetchMasterData('drinking', { page, limit, search: searchTerm, useSearchEndpoint: true }),

  searchSmoking: (searchTerm, page = 1, limit = 10) =>
    fetchMasterData('smoking', { page, limit, search: searchTerm, useSearchEndpoint: true }),

  searchIdeologies: (searchTerm, page = 1, limit = 10) =>
    fetchMasterData('ideologies', { page, limit, search: searchTerm, useSearchEndpoint: true }),

  searchFamilyTypes: (searchTerm, page = 1, limit = 10) =>
    fetchMasterData('familyTypes', { page, limit, search: searchTerm, useSearchEndpoint: true })
};
